<template>
  <div v-if="hasContent" class="contextual-help">
    <UiMenu ref="menuRef" align="left">
      <template #trigger>
        <UiButton
          :variant="helpVariant === 'outlined' ? 'secondary' : 'primary'"
          type="button"
          class="contextual-help__trigger"
          :title="helpTooltip"
        >
          ?
        </UiButton>
      </template>

      <UiCard class="contextual-help__card">
        <div v-if="title" class="contextual-help__card-header">
          <span class="contextual-help__card-title">Справка: {{ title }}</span>
          <UiButton variant="secondary" type="button" @click="closeMenu"> Закрыть </UiButton>
        </div>

        <div class="contextual-help__card-body">
          <div v-if="content" class="contextual-help__content">
            <p>{{ content }}</p>
          </div>
          <slot name="content" />

          <div v-if="steps?.length" class="contextual-help__steps">
            <div class="contextual-help__steps-title">Пошаговая инструкция:</div>
            <ol class="contextual-help__steps-list">
              <li v-for="(step, index) in steps" :key="index" class="contextual-help__step">
                {{ step }}
              </li>
            </ol>
          </div>

          <div v-if="links?.length" class="contextual-help__links">
            <div class="contextual-help__links-title">Полезные ссылки:</div>
            <div class="contextual-help__links-list">
              <a
                v-for="link in links"
                :key="link.url"
                :href="link.url"
                :target="link.external ? '_blank' : '_self'"
                rel="noopener noreferrer"
                class="contextual-help__link"
                @click="closeMenu"
              >
                {{ link.title }}
              </a>
            </div>
          </div>

          <div v-if="media" class="contextual-help__media">
            <img
              v-if="media.type === 'image'"
              :src="media.url"
              :alt="media.alt ?? ''"
              class="contextual-help__media-img"
            />
            <video
              v-else-if="media.type === 'video'"
              :src="media.url"
              controls
              class="contextual-help__media-video"
            />
          </div>

          <div v-if="relatedTutorials?.length" class="contextual-help__tutorials">
            <div class="contextual-help__tutorials-title">Связанные руководства:</div>
            <UiButton
              v-for="tutorialId in relatedTutorials"
              :key="tutorialId"
              variant="secondary"
              class="contextual-help__tutorial-btn"
              @click="startRelatedTutorial(tutorialId)"
            >
              {{ getTutorialName(tutorialId) }}
            </UiButton>
          </div>
        </div>

        <div v-if="showActions" class="contextual-help__actions">
          <UiButton
            v-if="showFeedback"
            variant="secondary"
            type="button"
            @click="showFeedbackDialog = true"
          >
            Полезно?
          </UiButton>
          <UiButton variant="secondary" type="button" @click="closeMenu"> Закрыть </UiButton>
        </div>
      </UiCard>
    </UiMenu>

    <UiModal v-model="showFeedbackDialog" title="Обратная связь" :max-width="400">
      <p class="contextual-help__feedback-lead">Была ли эта подсказка полезной?</p>
      <div class="contextual-help__rating">
        <button
          v-for="n in 5"
          :key="n"
          type="button"
          :class="[
            'contextual-help__rating-btn',
            { 'contextual-help__rating-btn--active': feedbackRating >= n },
          ]"
          @click="feedbackRating = n"
        >
          {{ n }}
        </button>
      </div>
      <UiTextarea
        v-model="feedbackComment"
        placeholder="Комментарий (необязательно)"
        :rows="3"
        class="contextual-help__feedback-comment"
      />
      <template #footer>
        <UiButton variant="secondary" @click="showFeedbackDialog = false">Отмена</UiButton>
        <UiButton variant="primary" @click="submitFeedback">Отправить</UiButton>
      </template>
    </UiModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useTutorial } from '@/modules/tutorial/public'
import UiMenu from '@/shared/ui/UiMenu.vue'
import UiCard from '@/shared/ui/UiCard.vue'
import UiButton from '@/shared/ui/UiButton.vue'
import UiModal from '@/shared/ui/UiModal.vue'
import UiTextarea from '@/shared/ui/UiTextarea.vue'

type Anchor =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'center'
  | 'center center'
  | 'top left'
  | 'top right'
  | 'top center'
  | 'bottom left'
  | 'bottom right'
  | 'bottom center'
  | 'left top'
  | 'left bottom'
  | 'left center'
  | 'right top'
  | 'right bottom'
  | 'right center'

interface HelpLink {
  title: string
  url: string
  external?: boolean
}

interface HelpMedia {
  type: 'image' | 'video'
  url: string
  alt?: string
}

interface Props {
  title?: string
  content?: string
  steps?: string[]
  links?: HelpLink[]
  media?: HelpMedia
  relatedTutorials?: string[]
  helpIcon?: string
  helpColor?: string
  helpVariant?: 'text' | 'outlined' | 'elevated' | 'tonal' | 'plain'
  size?: 'x-small' | 'small' | 'default' | 'large' | 'x-large'
  location?: Anchor
  offset?: number
  showActions?: boolean
  showFeedback?: boolean
  context?: string
  pageId?: string
}

const props = withDefaults(defineProps<Props>(), {
  helpVariant: 'text',
  size: 'small',
  location: 'bottom',
  offset: 8,
  showActions: true,
  showFeedback: false,
})

const { startTutorial, getTutorialById } = useTutorial()

const menuRef = ref<{ close: () => void } | null>(null)
const showFeedbackDialog = ref(false)
const feedbackRating = ref(0)
const feedbackComment = ref('')

const hasContent = computed(
  () =>
    !!(
      props.title ||
      props.content ||
      (props.steps && props.steps.length > 0) ||
      (props.links && props.links.length > 0)
    ),
)
const helpTooltip = computed(() => (props.title ? `Справка: ${props.title}` : 'Показать справку'))

const getTutorialName = (tutorialId: string) => {
  const tutorial = getTutorialById(tutorialId)
  return tutorial?.name || tutorialId
}

const closeMenu = () => {
  menuRef.value?.close()
}

const startRelatedTutorial = (tutorialId: string) => {
  closeMenu()
  startTutorial(tutorialId)
}

const submitFeedback = async () => {
  try {
    throw new Error('Feedback endpoint is not configured')
  } catch (error) {
    console.error('Error submitting feedback:', error)
  }
}
</script>

<style scoped>
.contextual-help {
  position: relative;
  display: inline-block;
}

.contextual-help__trigger {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.contextual-help__trigger:hover {
  opacity: 1;
}

.contextual-help__card {
  min-width: 280px;
  max-width: 400px;
  padding: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(15, 23, 42, 0.2);
  animation: contextual-help-appear 0.2s ease-out;
}

@keyframes contextual-help-appear {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contextual-help__card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.contextual-help__card-title {
  font-size: 0.9375rem;
  font-weight: 500;
}

.contextual-help__card-body {
  margin-bottom: 8px;
}

.contextual-help__content {
  line-height: 1.6;
  margin-bottom: 12px;
}

.contextual-help__content p {
  margin: 0;
  font-size: 0.875rem;
}

.contextual-help__steps-title,
.contextual-help__links-title,
.contextual-help__tutorials-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 6px;
}

.contextual-help__steps-list {
  margin: 0 0 12px 1.5rem;
  padding: 0;
}

.contextual-help__step {
  margin-bottom: 4px;
  font-size: 0.875rem;
}

.contextual-help__links-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
}

.contextual-help__link {
  font-size: 0.875rem;
  color: #0f172a;
  text-decoration: none;
}

.contextual-help__link:hover {
  text-decoration: underline;
}

.contextual-help__media {
  margin-top: 12px;
}

.contextual-help__media-img,
.contextual-help__media-video {
  max-height: 200px;
  width: 100%;
  border-radius: 6px;
  object-fit: contain;
}

.contextual-help__tutorials {
  margin-top: 12px;
}

.contextual-help__tutorial-btn {
  display: block;
  width: 100%;
  margin-bottom: 4px;
  text-align: left;
}

.contextual-help__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.contextual-help__feedback-lead {
  margin-bottom: 12px;
}

.contextual-help__rating {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.contextual-help__rating-btn {
  width: 36px;
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #fff;
  font: inherit;
  cursor: pointer;
}

.contextual-help__rating-btn:hover,
.contextual-help__rating-btn--active {
  background: #0f172a;
  color: #fff;
  border-color: #0f172a;
}

.contextual-help__feedback-comment {
  margin-top: 12px;
}

@media (max-width: 600px) {
  .contextual-help__card {
    max-width: calc(100vw - 32px);
  }
}
</style>
